<script setup lang="ts">
import FormInput from '@/Components/Molecules/FormInput.vue';
import FormTextarea from '@/Components/Molecules/FormTextarea.vue';
import { useFormValidation } from '@/composables/useFormValidation';
import { useTranslations } from '@/composables/useTranslations';
import { useForm } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const { t } = useTranslations();
const { validators } = useFormValidation();

const form = useForm({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
});

// Loosen error typing to allow backend 'general' errors
const formErrors = computed<Record<string, string>>(() => form.errors as Record<string, string>);

const validationErrors = ref<Record<string, string>>({});
const showValidation = ref(false);
const submitSuccess = ref(false);

// Validation rules
const validateField = (field: string, value: string): boolean => {
    const errors: string[] = [];

    if (!value.trim()) {
        errors.push(t('validation.required'));
    } else if (field === 'email' && !validators.email(value)) {
        errors.push(t('validation.invalidEmail'));
    } else if (field === 'phone' && value && !validators.phone(value)) {
        errors.push(t('validation.invalidPhone'));
    } else if (field === 'name' && !validators.minLength(3)(value)) {
        errors.push(t('validation.minLength', { min: '3' }));
    } else if (field === 'subject' && !validators.minLength(5)(value)) {
        errors.push(t('validation.minLength', { min: '5' }));
    } else if (field === 'message' && !validators.minLength(10)(value)) {
        errors.push(t('validation.minLength', { min: '10' }));
    }

    if (errors.length > 0) {
        validationErrors.value[field] = errors[0];
        return false;
    } else {
        delete validationErrors.value[field];
        return true;
    }
};

const validateForm = (): boolean => {
    showValidation.value = true;
    let isValid = true;

    isValid = validateField('name', form.name) && isValid;
    isValid = validateField('email', form.email) && isValid;
    isValid = validateField('subject', form.subject) && isValid;
    isValid = validateField('message', form.message) && isValid;

    if (form.phone) {
        isValid = validateField('phone', form.phone) && isValid;
    }

    return isValid;
};

const submit = () => {
    validationErrors.value = {};

    if (!validateForm()) {
        return;
    }

    form.post('/contact', {
        preserveScroll: true,
        onSuccess: () => {
            submitSuccess.value = true;
            form.reset();
            showValidation.value = false;
            setTimeout(() => {
                submitSuccess.value = false;
            }, 5000);
        },
        onError: () => {
            // Errors are handled by the form object
        },
    });
};
</script>

<template>
    <section class="bg-primary-lighter py-16">
        <div class="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="mx-auto max-w-4xl space-y-6 rounded-3xl bg-primary-light p-8 shadow-lg">
                <h2 class="mb-6 text-center text-3xl font-bold text-dark md:text-4xl">{{ t('contact.title') }}</h2>

                <!-- Success Message -->
                <div v-if="submitSuccess" class="rounded-2xl border border-green-200 bg-white p-4 text-green-800 shadow-sm">
                    {{ t('contact.successMessage') }}
                </div>

                <!-- Error Message -->
                <div v-if="formErrors.general" class="rounded-2xl border border-red-200 bg-white p-4 text-red-800 shadow-sm">
                    {{ formErrors.general }}
                </div>

                <form @submit.prevent="submit" class="space-y-6 rounded-2xl p-6 md:p-8">
                    <FormInput
                        v-model="form.name"
                        :label="t('contact.name')"
                        :placeholder="t('contact.namePlaceholder')"
                        :error="showValidation ? validationErrors.name : undefined"
                        :show-error="showValidation"
                        required
                        @blur="validateField('name', form.name)"
                    />

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <FormInput
                            v-model="form.email"
                            :label="t('contact.email')"
                            :placeholder="t('contact.emailPlaceholder')"
                            type="email"
                            :error="showValidation ? validationErrors.email : undefined"
                            :show-error="showValidation"
                            required
                            @blur="validateField('email', form.email)"
                        />
                        <FormInput
                            v-model="form.phone"
                            :label="t('contact.phone')"
                            :placeholder="t('contact.phonePlaceholder')"
                            type="tel"
                            :hint="t('contact.phoneHint')"
                            :error="showValidation ? validationErrors.phone : undefined"
                            :show-error="showValidation"
                            @blur="validateField('phone', form.phone)"
                        />
                    </div>

                    <FormInput
                        v-model="form.subject"
                        :label="t('contact.subject')"
                        :placeholder="t('contact.subjectPlaceholder')"
                        :error="showValidation ? validationErrors.subject : undefined"
                        :show-error="showValidation"
                        required
                        @blur="validateField('subject', form.subject)"
                    />

                    <FormTextarea
                        v-model="form.message"
                        :label="t('contact.message')"
                        :placeholder="t('contact.messagePlaceholder')"
                        :error="showValidation ? validationErrors.message : undefined"
                        :show-error="showValidation"
                        required
                        @blur="validateField('message', form.message)"
                    />

                    <div class="text-center">
                        <button
                            type="submit"
                            :disabled="form.processing"
                            :aria-busy="form.processing"
                            class="btn btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {{ form.processing ? t('contact.sending') : t('contact.send') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>
